/**
 * Mock Platform Dashboard API module. Calls /api/admin/mock/** — backend
 * endpoints only exist when `merkflow.mock.enabled=true` on the server.
 * On prod every call 404s (controller bean never instantiates).
 *
 * Frontend feature-gates this whole module on the `mockEnabled` flag
 * returned by `/api/admin/config`. Fetching that flag is the first thing
 * the Mock Platform page does — so we never hit these endpoints on a
 * misconfigured env.
 */
import { api } from '@/lib/api';

export type MockPlatform = 'facebook' | 'instagram' | 'linkedin' | 'x';

export interface MockAccount {
  id: string;
  platform: MockPlatform;
  externalId: string;
  displayName: string;
  accountType: string;
  avatarUrl: string | null;
  followerCount: number;
  createdAt: string | null;
  activeToken: {
    token: string;
    issuedAt: string | null;
    expiresAt: string | null;
  } | null;
}

export interface MockPost {
  id: string;
  accountId: string;
  externalId: string;
  content: string | null;
  mediaUrls: string[];
  postType: string;
  permalink: string | null;
  publishedAt: string | null;
  metrics: string; // JSON string
  rawRequest: string | null;
}

export interface MockEngagementItem {
  id: string;
  accountId: string;
  postId: string | null;
  itemType: string;
  externalId: string;
  fromUsername: string;
  content: string;
  createdAt: string | null;
  deliveredAt: string | null;
  deliveryMethod: string | null;
}

export interface MockApiCall {
  id: number;
  platform: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  requestHeaders: string | null;
  requestBody: string | null;
  responseBody: string | null;
  injectedError: string | null;
  receivedAt: string | null;
}

export interface MockErrorInjection {
  id: string;
  platform: string;
  endpointPattern: string;
  errorType: 'token_invalid' | 'rate_limited' | 'server_error' | 'validation_error';
  remainingHits: number;
  createdAt: string | null;
}

export const mockApi = {
  listAccounts: () => api.get<MockAccount[]>('/api/admin/mock/accounts').then((r) => r.data),
  createAccount: (body: {
    platform: MockPlatform;
    externalId?: string;
    displayName: string;
    accountType: string;
    followerCount?: number;
    avatarUrl?: string;
  }) => api.post<MockAccount>('/api/admin/mock/accounts', body).then((r) => r.data),
  rotateToken: (accountId: string) =>
    api.post<MockAccount>(`/api/admin/mock/accounts/${accountId}/rotate-token`).then((r) => r.data),
  revokeToken: (accountId: string) =>
    api.post<MockAccount>(`/api/admin/mock/accounts/${accountId}/revoke-token`).then((r) => r.data),
  deleteAccount: (accountId: string) =>
    api.delete(`/api/admin/mock/accounts/${accountId}`).then((r) => r.data),
  updateAccount: (
    accountId: string,
    body: {
      displayName?: string;
      accountType?: string;
      externalId?: string;
      followerCount?: number;
      avatarUrl?: string;
    },
  ) => api.patch<MockAccount>(`/api/admin/mock/accounts/${accountId}`, body).then((r) => r.data),

  listPosts: (accountId?: string, limit = 100) =>
    api
      .get<MockPost[]>('/api/admin/mock/posts', { params: { accountId, limit } })
      .then((r) => r.data),
  updatePostMetrics: (postId: string, metrics: Record<string, number>) =>
    api.patch<MockPost>(`/api/admin/mock/posts/${postId}/metrics`, metrics).then((r) => r.data),
  randomiseMetrics: () =>
    api.post<number>('/api/admin/mock/posts/randomise-metrics').then((r) => r.data),

  listEngagement: (limit = 50) =>
    api
      .get<MockEngagementItem[]>('/api/admin/mock/engagement', { params: { limit } })
      .then((r) => r.data),
  dropEngagement: (body: {
    accountId: string;
    postId?: string | null;
    itemType: 'comment' | 'mention' | 'dm';
    fromUsername: string;
    content: string;
  }) => api.post<MockEngagementItem>('/api/admin/mock/engagement', body).then((r) => r.data),
  replayEngagement: (id: string) =>
    api.post<string>(`/api/admin/mock/engagement/${id}/replay`).then((r) => r.data),

  listApiCalls: (platform?: string, limit = 200) =>
    api
      .get<MockApiCall[]>('/api/admin/mock/api-calls', { params: { platform, limit } })
      .then((r) => r.data),

  listInjections: () =>
    api.get<MockErrorInjection[]>('/api/admin/mock/error-injections').then((r) => r.data),
  createInjection: (body: {
    platform: string;
    endpointPattern: string;
    errorType: MockErrorInjection['errorType'];
    remainingHits?: number;
  }) => api.post<MockErrorInjection>('/api/admin/mock/error-injections', body).then((r) => r.data),
  clearInjection: (id: string) =>
    api.delete(`/api/admin/mock/error-injections/${id}`).then((r) => r.data),

  reset: () => api.post('/api/admin/mock/reset').then((r) => r.data),
};

/** Read-only server config — drives env banner + nav gating. Unauth OK. */
export interface AdminConfig {
  mockEnabled: boolean;
}
export const adminConfigApi = {
  get: () => api.get<AdminConfig>('/api/admin/config').then((r) => r.data),
};
